import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ContentContainer from "../../templates/content-container/contentContainer";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import Tag from "../../ui-components/molecules/tag/tag";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../app/data/common/queryKeys";
import releaseDetailsService from "../../app/data/release-details/releaseDetailsService";
import { normalizeQuillHtml } from "../../services/common/normalizeQuillHtml";
import { Routes } from "../../app/route/RoutesConfig";
import {
  audienceLabel,
  formatDay,
  formatFileSize,
  formatTimestamp,
  statusColor,
  statusLabel
} from "./releaseDetailsDisplay";
import "./releaseDetails.scss";

const ReleaseDetailsPage: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [downloadingId, setDownloadingId] = useState("");
  const [downloadError, setDownloadError] = useState("");

  const detailsQuery = useQuery({
    queryKey: [QUERY_KEYS.RELEASE_DETAILS_DETAIL, id],
    queryFn: () => releaseDetailsService.get(id),
    enabled: !!id
  });

  const download = (attachmentId: string, fileName: string) => {
    setDownloadError("");
    setDownloadingId(attachmentId);
    releaseDetailsService.downloadAttachment(attachmentId).then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }).catch((error: Error) => {
      setDownloadError(error.message || "Unable to download the file");
    }).finally(() => {
      setDownloadingId("");
    });
  };

  const details = detailsQuery.data;
  const period = [formatDay(details?.from), formatDay(details?.to)].filter(Boolean).join(" – ");
  const created = formatTimestamp(details?.dateCreated);
  const updated = formatTimestamp(details?.dateUpdated);
  const audience = audienceLabel(details?.recipientType);

  return (
    <ContentContainer
      pageId={PAGE_IDS.RELEASE_DETAILS}
      title={details?.header || "Release Details"}
      isLoading={detailsQuery.isLoading}
    >
      <div className="xgs-release-details">
        <button
          type="button"
          className="xgs-release-details__back"
          onClick={() => history.push(Routes.releaseDetails)}
        >
          All release details
        </button>
        {detailsQuery.isError && (
          <XGSErrorMessage>{(detailsQuery.error as Error)?.message || "Unable to load release details"}</XGSErrorMessage>
        )}
        {details && (
          <article className="xgs-release-details__article">
            <div className="xgs-release-details__facts">
              {details.status && (
                <Tag mods={{ color: statusColor(details.status) }}>{statusLabel(details.status)}</Tag>
              )}
              {audience && <span className="xgs-release-details__audience">{audience}</span>}
              {created && (
                <span>
                  Created {created}
                  {details.userCreated ? ` by ${details.userCreated}` : ""}
                </span>
              )}
              {updated && <span>Updated {updated}</span>}
              {period && <span>Announcement {period}</span>}
            </div>
            <div
              className="xgs-release-details__body ql-editor"
              dangerouslySetInnerHTML={{ __html: normalizeQuillHtml(details.body) }}
            />
            {details.attachments && details.attachments.length > 0 && (
              <div className="xgs-release-details__files">
                <div className="xgs-release-details__files-title">Files</div>
                <div className="xgs-release-details__file-list">
                  {details.attachments.map((attachment) => (
                    <button
                      type="button"
                      className="xgs-release-details__file"
                      key={attachment.id}
                      onClick={() => download(attachment.id, attachment.fileName)}
                      disabled={downloadingId === attachment.id}
                    >
                      <span className="xgs-release-details__file-name">{attachment.fileName}</span>
                      <span className="xgs-release-details__file-meta">
                        {formatFileSize(attachment.size)}
                        <span className="xgs-release-details__file-action">
                          {downloadingId === attachment.id ? "Downloading..." : "Download"}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                {downloadError && <XGSErrorMessage>{downloadError}</XGSErrorMessage>}
              </div>
            )}
          </article>
        )}
      </div>
    </ContentContainer>
  );
};

export default ReleaseDetailsPage;
